/**
 * Soft≠Live Page163 — SA Audit residual liquid (page85/124 buses + status).
 * Mount: /sa/audit
 * Buses:
 *   GET /sa/audit/status (always-on Live posture)
 *   GET /audit/events · GET /audit/events/{id}
 *   GET /sa/catalog (risk vocabulary when catalog_allowed)
 * Soft residual deleted:
 *  - sandpit trail without liquid rail / KPI / Re-read / status SoT
 *  - Soft invent trail when list gated
 *  - Soft brochure severity without Live/catalog honesty
 */
import { useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, Sec, go } from "./ui.js";
import { path, useApi, usePolled } from "./api.js";
import { useSession } from "./session.js";
import { Unavailable } from "./sa-shared.js";
import { formatUserInstant } from "./time.js";

/** Pure: risk level from Live catalog prefixes. */
export function auditLevelOf(action, risky) {
  const s = String(action || "").toLowerCase();
  const prefixes = Array.isArray(risky) ? risky : [];
  if (s.includes("error") || s.includes("crash")) return "error";
  if (prefixes.some((p) => s.startsWith(String(p || "").toLowerCase()))) return "warn";
  return ["fail", "reject", "revoke", "rollback", "block"].some((w) => s.includes(w))
    ? "warn"
    : "info";
}

/** Pure: list rows. */
export function auditRowsOf(pack) {
  const rows = (pack && pack.items) || [];
  return Array.isArray(rows) ? rows : [];
}

function clock(ts) {
  return ts ? formatUserInstant(ts, { date: false }) : "—";
}

/** Pure: KPI strip from Live trail + catalog only. */
export function auditKpisOf(rows, warnN, actors, catalogKeys, known) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    rows: list.length,
    warn: Number(warnN) || 0,
    actors: Number(actors) || 0,
    domains: known ? (Number(catalogKeys) || 0) : null,
  };
}

/** Pure: client seat guess when status not yet loaded. */
export function canReadAuditOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "superadmin" || r === "admin") return true;
  if (r !== "sa_staff") return false;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) =>
    /sa_audit_read|sa_full_access|sa_full/i.test(String(c || "").trim()),
  );
}

export function SaAuditDesk() {
  const { me } = useSession();
  const posture = useApi(path("get__sa_audit_status"));
  // Fail-closed while loading — Soft invent audit list deleted.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const seat = post.list_allowed === true;
  const catalogAllowed = post.catalog_allowed === true;
  const detail = post.detail || null;

  const [sev, setSev] = useState("all");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [target, setTarget] = useState("");
  const [applied, setApplied] = useState({ actor: "", action: "", target: "" });
  const [selectedId, setSelectedId] = useState(null);

  const query = { limit: 200 };
  if (applied.actor) query.actor = applied.actor;
  if (applied.action) query.action = applied.action;
  if (applied.target) query.target = applied.target;

  const listPath = seat ? path("get__audit_events", { query }) : "";
  const log = usePolled(listPath, 20000);
  // Live catalog_allowed only — client invent before status lands deleted.
  const wantCatalog = catalogAllowed;
  const cat = useApi(wantCatalog ? path("get__sa_catalog") : "");

  const detailPath = seat && selectedId
    ? path("get__audit_events_p", { pathParams: [String(selectedId)] })
    : null;
  const det = useApi(detailPath);

  const risky = (cat.data && cat.data.high_risk_actions) || [];
  const keys = (cat.data && cat.data.audit_event_keys) || {};
  const all = useMemo(() => auditRowsOf(log.data), [log.data]);
  const rows = useMemo(() => {
    if (sev === "all") return all;
    return all.filter((r) => {
      const lv = auditLevelOf(r.action, risky);
      return sev === "warn" ? lv !== "info" : lv === "info";
    });
  }, [all, sev, risky]);

  const warnN = all.filter((r) => auditLevelOf(r.action, risky) !== "info").length;
  const actors = useMemo(
    () => [...new Set(all.map((r) => String(r.actor || "").trim()).filter(Boolean))].length,
    [all],
  );
  const kpis = useMemo(
    () => auditKpisOf(all, warnN, actors, Object.keys(keys).length, !cat.error && !!cat.data),
    [all, warnN, actors, keys, cat.error, cat.data],
  );

  const applyFilters = () => {
    setSelectedId(null);
    setApplied({
      actor: actor.trim(),
      action: action.trim(),
      target: target.trim(),
    });
  };

  const clearFilters = () => {
    setActor("");
    setAction("");
    setTarget("");
    setApplied({ actor: "", action: "", target: "" });
    setSelectedId(null);
  };

  const reRead = () => {
    posture.reload && posture.reload();
    log.reload && log.reload();
    cat.reload && cat.reload();
    det.reload && det.reload();
  };

  const denied = !!(log.error && /\b40[13]\b|forbidden|insufficient/i.test(log.error || ""));
  const detailRow = det.data && typeof det.data === "object" ? det.data : null;
  const detailJson = detailRow
    ? JSON.stringify(
      {
        id: detailRow.id,
        actor: detailRow.actor,
        action: detailRow.action,
        target: detailRow.target,
        correlation_id: detailRow.correlation_id,
        created_at: detailRow.created_at,
        detail: detailRow.detail || detailRow.humanized || {},
      },
      null,
      2,
    )
    : "";

  if (!seat) {
    return html`<div className="sa-audit-desk sa-audit-desk--liquid"
      data-cmc-page="page124-sa-audit" data-cmc-legacy="page85-sa-audit" data-cmc-audit="page55"
      data-cmc-residual="page163-audit">
      <div className="sa-audit-desk__rail">
        <div>
          <p className="eyebrow">Platform · Audit</p>
          <h2 className="sa-audit-desk__title">Seat closed</h2>
          <p className="note">Audit trail stays empty until this seat opens.</p>
        </div>
        <div className="btn-row sa-audit-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading}>
            <${Ico} d="refresh" w=${13}/> Re-read posture
          </button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/settings/audit")}>Export desk</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/caps")}>Capabilities</button>
        </div>
      </div>
      <${Card} title="Audit posture" meta="Live">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${String(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            List sealed · this desk never writes
            · ${detail || "not available on this account"}
          </p>` : null}
      <//>
      <${Alert} tone="info" title="Audit trail not on this account"
        say=${"Audit trail needs a manager or staff audit seat. This account is "
          + (me && me.role ? me.role : "operator")
          + " — trail rows are not invented locally."}/>
      <${Card} title="Event trail" meta="gated">
        <p className="note">No audit rows are offered on this grant.</p>
      <//>
    </div>`;
  }

  return html`<div className="sa-audit-desk sa-audit-desk--liquid"
    data-cmc-page="page124-sa-audit" data-cmc-legacy="page85-sa-audit" data-cmc-audit="page55"
    data-cmc-residual="page163-audit">
    <div className="sa-audit-desk__rail">
      <div>
        <p className="eyebrow">Platform · Audit</p>
        <h2 className="sa-audit-desk__title">Audit trail · server severity</h2>
        <p className="note">
          Rows load from the live audit bus when your seat allows.
          High-risk prefixes come from the platform catalog when that seat is open.
        </p>
      </div>
      <div className="btn-row sa-audit-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${!!log.loading || posture.loading}>
          <${Ico} d="refresh" w=${13}/> Re-read
        </button>
        <button type="button" className="btn btn--go btn--sm" onClick=${() => go("/settings/audit")}>
          Open audit export
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => go("/sa/caps")}>
          Capabilities
        </button>
      </div>
    </div>

    <${Card} title="Audit posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-audit-desk__kpis" role="group" aria-label="Audit posture">
          ${[
            ["Seat", seat ? "open" : "gated", seat ? "1" : "0", "status Live"],
            ["Catalog", catalogAllowed ? "armed" : "—", catalogAllowed ? "1" : "0", "risk vocab"],
            ["Humanize", post.humanize_enabled ? "on" : "off", post.humanize_enabled ? "1" : "0", "flag Live"],
            ["Write", "none", "0", "read-only trail"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-audit-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          List and export seats · trail is read-only on this desk
        </p>` : null}
    <//>

    <${Alert} tone="info" title="Audit trail"
      say="List, detail and catalog come from the server. Empty means nothing was returned for this grant."/>

    <div className="sa-audit-desk__kpis" role="group" aria-label="Audit KPIs">
      <div className="sa-audit-desk__kpi" data-on=${kpis.rows ? "1" : "0"}>
        <span>Rows loaded</span>
        <b>${log.loading && !log.data ? "…" : kpis.rows}</b>
      </div>
      <div className="sa-audit-desk__kpi" data-on=${kpis.warn ? "1" : "0"}>
        <span>High risk (view)</span>
        <b>${kpis.warn}</b>
      </div>
      <div className="sa-audit-desk__kpi" data-on=${kpis.actors ? "1" : "0"}>
        <span>Actors in page</span>
        <b>${kpis.actors}</b>
      </div>
      <div className="sa-audit-desk__kpi" data-on=${kpis.domains ? "1" : "0"}>
        <span>Catalog domains</span>
        <b>${kpis.domains == null ? "—" : kpis.domains}</b>
      </div>
    </div>

    ${denied
      ? html`<${Alert} tone="info" title="Audit list refused"
          say=${"Your account cannot read the audit trail. " + String(log.error || "")}/>`
      : null}
    ${log.error && !denied
      ? html`<${Alert} tone="warn" title="Audit list error"
          say=${String(log.error)}/>`
      : null}

    <div className="sa-audit-desk__grid">
      <${Card} title="Event trail"
          meta=${rows.length + " shown filters"}
          sub="Filter by action prefix and exact actor or target. Empty results stay empty.">
        <div className="sa-audit-desk__filters">
          <${Field} label="Actor">
            <${Input} icon="user" value=${actor}
              onInput=${(e) => setActor(e.target.value)}
              onKeyDown=${(e) => { if (e.key === "Enter") applyFilters(); }}
              placeholder="username"/>
          <//>
          <${Field} label="Action prefix">
            <${Input} icon="chip" value=${action}
              onInput=${(e) => setAction(e.target.value)}
              onKeyDown=${(e) => { if (e.key === "Enter") applyFilters(); }}
              placeholder="auth."/>
          <//>
          <${Field} label="Target">
            <${Input} icon="radio" value=${target}
              onInput=${(e) => setTarget(e.target.value)}
              onKeyDown=${(e) => { if (e.key === "Enter") applyFilters(); }}
              placeholder="device or user"/>
          <//>
        </div>
        <div className="btn-row" style=${{ marginBottom: 12 }}>
          <button type="button" className="btn btn--sm btn--go" onClick=${applyFilters}>
            Apply filters
          </button>
          <button type="button" className="btn btn--sm btn--ghost" onClick=${clearFilters}>
            Clear filters
          </button>
        </div>
        <div className="chips" style=${{ marginBottom: 14 }}>
          ${[["all", "Everything"], ["warn", "High risk"], ["info", "Routine"]].map(([id, l]) => html`
            <button key=${id} type="button" className="chip"
              data-sel=${sev === id ? "1" : "0"}
              onClick=${() => setSev(id)}>${l}</button>`)}
        </div>
        ${!all.length
          ? html`<${Unavailable} r=${log} what="the audit log"/>`
          : html`<div className="sa-audit-desk__table-wrap">
              <table className="tbl">
                <thead>
                  <tr><th>Time</th><th>Actor</th><th>Action</th><th>Target</th><th></th></tr>
                </thead>
                <tbody>
                  ${rows.map((r, i) => {
                    const lv = auditLevelOf(r.action, risky);
                    const id = r.id;
                    const on = selectedId != null && String(selectedId) === String(id) ? "1" : "0";
                    return html`
                      <tr key=${id || i} data-on=${on}>
                        <td><span className="m">${clock(r.created_at)}</span></td>
                        <td><b>${r.actor || "—"}</b></td>
                        <td><span className="m"
                          style=${lv !== "info"
                            ? { color: lv === "error" ? "var(--red)" : "var(--amber)" }
                            : null}>${r.action}</span></td>
                        <td><span className="m">${r.target || "—"}</span></td>
                        <td>
                          <button type="button" className="btn btn--ghost btn--sm"
                            disabled=${id == null}
                            onClick=${() => setSelectedId(id)}>
                            Detail
                          </button>
                        </td>
                      </tr>`;
                  })}
                </tbody>
              </table>
            </div>`}
      <//>

      <${F}>
        <${Card} title="Event detail"
            meta=${selectedId != null ? ("#" + selectedId) : "pick a row"}
            sub="Row detail only.">
          ${!selectedId
            ? html`<p className="note">Select Detail on a row.</p>`
            : det.loading && !detailRow
              ? html`<p className="note">Reading detail…</p>`
              : det.error
                ? html`<${Alert} tone="bad" title="Detail refused"
                    say=${String(det.error)}/>`
                : detailRow
                  ? html`<${F}>
                      <${KV} k="Actor" v=${String(detailRow.actor || "—")}/>
                      <${KV} k="Action" v=${String(detailRow.action || "—")}/>
                      <${KV} k="Target" v=${String(detailRow.target || "—")}/>
                      <${KV} k="Correlation" v=${String(detailRow.correlation_id || "—")}/>
                      <${KV} k="Created" v=${String(detailRow.created_at || "—")}/>
                      <pre className="sa-audit-desk__pre">${detailJson}</pre>
                    <//>`
                  : html`<p className="note">No detail payload for this row.</p>`}
        <//>

        <${Card} title="Event keys"
            meta=${Object.keys(keys).length ? Object.keys(keys).length + " domains" : null}
            sub="Server-exported risk vocabulary. Blank when the catalogue seat is closed.">
          ${!Object.keys(keys).length
            ? html`<${Unavailable} r=${cat} what="the audit vocabulary"/>`
            : Object.entries(keys).map(([g, ks]) => html`
              <${Sec} key=${g} title=${g} meta=${String((ks || []).length)}>
                <div className="chips">${(ks || []).map((k) => html`
                  <button key=${k} type="button" className="chip" data-tone="off"
                    onClick=${() => { setAction(String(k)); }}>${k}</button>`)}
                </div>
              <//>`)}
        <//>

        <${Card} title="Forced to warn"
            meta=${risky.length ? risky.length + " prefixes" : null}
            sub="Prefix-matched actions escalate to warn in the table.">
          ${!risky.length
            ? html`<${Unavailable} r=${cat} what="the risk classification"/>`
            : html`<div className="chips">${risky.map((k) => html`
                <button key=${k} type="button" className="chip" data-tone="warn"
                  onClick=${() => { setAction(String(k)); }}>${k}</button>`)}
              </div>`}
        <//>
      <//>
    </div>
  </div>`;
}
